/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, useTranslate } from "@adapptio/lib-components";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, { FC, useCallback, useContext, useState, useEffect, useRef } from "react";
import { termsEditorCommon } from "../../../terms/common";
import { EditorMainContext } from "../../contexts/EditorMainContext";
import { useEscKey } from "../../hooks/useEscKey";
import { FC_ANCHOR, FC_DIRECTION } from "../FloatingContainer";
import { Icon, ICON_SIZE } from "../Icon";
import { IResourceNode, ResourceList, RESOURCE_LIST_STYLE } from "../ResourceList";
import { FieldFloatingPanel, FIELD_FLOATING_PANEL_STYLE } from "./FieldFloatingPanel";
import { IFieldValidationError } from "./FieldTypes";
import { FieldWrapper } from "./FieldWrapper";
import { FieldValidationErrors } from "./FieldValidationErrors";

export interface IFieldResourceProps {
	value: string;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	allowedResourceTypes?: RESOURCE_TYPES[];
	className?: string;
	grow?: boolean;
	listAnchor?: FC_ANCHOR;
	listDirection?: FC_DIRECTION;
	listOffset?: number;
	onChange?: (value: string) => void;
}

export const FieldResource: FC<IFieldResourceProps> = ({
	value,
	placeholder,
	readOnly,
	validationErrors,
	allowedResourceTypes,
	className,
	grow,
	listAnchor,
	listDirection,
	listOffset,
	onChange
}) => {
	const editorCtx = useContext(EditorMainContext);
	const t = useTranslate();

	const [ isListOpened, setIsListOpened ] = useState(false);
	const elRef = useRef<HTMLButtonElement>();

	const [ label, setLabel ] = useState(() => {
		return value
			? editorCtx.resourceService.getResourceDescriptorById(value)?.name ||
					t("editor", termsEditorCommon.fields.resource.notFound)
			: t("editor", placeholder) || t("editor", termsEditorCommon.fields.resource.select);
	});

	const toggleResourceList = useCallback(() => {
		setIsListOpened((x) => !x);
	}, []);

	const closeResourceList = useCallback(() => {
		setIsListOpened(false);
	}, []);

	const handleSelect = useCallback(
		(resourceNode: IResourceNode) => {
			onChange(resourceNode.id);
			setIsListOpened(false);
		},
		[ onChange ]
	);

	useEscKey(() => setIsListOpened(false), isListOpened);

	useEffect(() => {
		if (value) {
			const resource = editorCtx.resourceService.getResourceById(value);

			if (resource) {
				const handleChange = () => setLabel(resource.descriptor.name);
				const handleDelete = () => setLabel(t("editor", termsEditorCommon.fields.resource.deleted));

				handleChange();

				onEvent(resource.onChange, handleChange);
				onEvent(resource.onDelete, handleDelete);

				return () => {
					offEvent(resource.onChange, handleChange);
					offEvent(resource.onDelete, handleDelete);
				};
			} else {
				setLabel(t("editor", termsEditorCommon.fields.resource.notFound));
			}
		} else {
			setLabel(t("editor", placeholder) || t("editor", termsEditorCommon.fields.resource.select));
		}
	}, [ editorCtx.resourceService, value ]);

	const baseClass = BEM("edt-field", "selector", {
		"is-empty": !value,
		"is-opened": isListOpened,
		readonly: readOnly
	});

	baseClass.add("edt-field-control");

	return (
		<FieldWrapper type="resource" className={className} grow={grow} isFocused={isListOpened}>
			<button
				className={baseClass.toClassName()}
				onClick={toggleResourceList}
				disabled={readOnly}
				ref={elRef}
				title={label}
			>
				<span className={baseClass.element("selector-label").toClassName()}>{label}</span>
				<FieldValidationErrors errors={validationErrors} />
				<Icon name="mdi/folder-open" size={ICON_SIZE.CUSTOM} />
			</button>
			<FieldFloatingPanel
				for={elRef}
				isOpened={isListOpened}
				anchor={listAnchor || FC_ANCHOR.RIGHT_BOTTOM}
				direction={listDirection || FC_DIRECTION.HR}
				style={FIELD_FLOATING_PANEL_STYLE.STICKY}
				offset={listOffset || 1}
				className="edt-field--type-resource__panel"
				onClose={closeResourceList}
			>
				<ResourceList
					onSelect={handleSelect}
					labelSearch="Select target directory"
					style={RESOURCE_LIST_STYLE.COMPACT}
					allowedResourceTypes={allowedResourceTypes}
					isActive={true}
				/>
			</FieldFloatingPanel>
		</FieldWrapper>
	);
};
